import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Alert, Tabs, Tab, Spinner, Table, Badge, Row, Col, Pagination, Form } from 'react-bootstrap';
import { 
    migrateUsersApi, 
    migrateProjectsApi, 
    migrateRolesApi,
    getMigrationStatsApi,
    getStagedUsersApi,
    getMigrationImportLogsApi,
    getMigrationEmailLogsApi
} from '../../services/adminApi';
import { 
    FiUploadCloud, 
    FiDownload, 
    FiFileText, 
    FiMail, 
    FiCheckCircle, 
    FiClock, 
    FiRefreshCw, 
    FiUsers, 
    FiAlertCircle,
    FiXCircle,
    FiList
} from 'react-icons/fi';
import Swal from 'sweetalert2';
import './MigrationPage.css';

const MigrationPage = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('users');
    const fileInputRef = useRef(null);

    // 📊 Re-launch Campaign State
    const [stats, setStats] = useState({
        total_staged: 0,
        registered: 0,
        pending: 0,
        total_emails_sent: 0,
        schedule_times: ['09:00 AM IST', '02:00 PM IST', '07:00 PM IST']
    });
    const [statsLoading, setStatsLoading] = useState(false);

    // 📑 Sub-tab toggle: 'queue' | 'import_logs' | 'email_logs'
    const [subTab, setSubTab] = useState('queue');

    // 📋 Staged Candidates Table State
    const [stagedUsers, setStagedUsers] = useState([]);
    const [stagedLoading, setStagedLoading] = useState(false);
    const [stagedPage, setStagedPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // 📥 Import Logs State (Success & Failure with Reason)
    const [importLogs, setImportLogs] = useState([]);
    const [importLogsLoading, setImportLogsLoading] = useState(false);
    const [importLogsPage, setImportLogsPage] = useState(1);
    const [importLogsTotalPages, setImportLogsTotalPages] = useState(1);
    const [importLogsTotal, setImportLogsTotal] = useState(0);
    const [importStatusFilter, setImportStatusFilter] = useState('ALL');

    // ✉️ Email Dispatch Logs State (Success & Failure with Reason)
    const [emailLogs, setEmailLogs] = useState([]);
    const [emailLogsLoading, setEmailLogsLoading] = useState(false);
    const [emailLogsPage, setEmailLogsPage] = useState(1);
    const [emailLogsTotalPages, setEmailLogsTotalPages] = useState(1);
    const [emailLogsTotal, setEmailLogsTotal] = useState(0);
    const [emailStatusFilter, setEmailStatusFilter] = useState('ALL');


    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    // Load Campaign Stats
    const fetchStats = async () => {
        try {
            setStatsLoading(true);
            const res = await getMigrationStatsApi();
            if (res.data?.success && res.data?.data) {
                setStats(res.data.data);
            } else if (res.data?.total_staged !== undefined) {
                setStats(res.data);
            }
        } catch (err) {
            console.error("Failed to load migration stats", err);
        } finally {
            setStatsLoading(false);
        }
    };

    // Load Staged Users List
    const fetchStagedUsers = async (page = 1) => {
        try {
            setStagedLoading(true);
            const res = await getStagedUsersApi({ page, limit: 10 });
            const list = res.data?.data || res.data?.rows || (Array.isArray(res.data) ? res.data : []);
            setStagedUsers(list);
            setStagedPage(page);
            if (res.data?.totalPages) setTotalPages(res.data.totalPages);
            if (res.data?.total !== undefined) setTotalRecords(res.data.total);
        } catch (err) {
            console.error("Failed to load staged users", err);
        } finally {
            setStagedLoading(false);
        }
    };

    // Load Import Logs
    const fetchImportLogs = async (page = 1, status = importStatusFilter) => {
        try {
            setImportLogsLoading(true);
            const res = await getMigrationImportLogsApi({ page, limit: 10, status });
            const list = res.data?.data || res.data?.rows || (Array.isArray(res.data) ? res.data : []);
            setImportLogs(list);
            setImportLogsPage(page);
            if (res.data?.totalPages) setImportLogsTotalPages(res.data.totalPages);
            if (res.data?.total !== undefined) setImportLogsTotal(res.data.total);
        } catch (err) {
            console.error("Failed to load import logs", err);
        } finally {
            setImportLogsLoading(false);
        }
    };

    // Load Email Logs
    const fetchEmailLogs = async (page = 1, status = emailStatusFilter) => {
        try {
            setEmailLogsLoading(true);
            const res = await getMigrationEmailLogsApi({ page, limit: 10, status });
            const list = res.data?.data || res.data?.rows || (Array.isArray(res.data) ? res.data : []);
            setEmailLogs(list);
            setEmailLogsPage(page);
            if (res.data?.totalPages) setEmailLogsTotalPages(res.data.totalPages);
            if (res.data?.total !== undefined) setEmailLogsTotal(res.data.total);
        } catch (err) {
            console.error("Failed to load email logs", err);
        } finally {
            setEmailLogsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'users') {
            fetchStats();
            if (subTab === 'queue') fetchStagedUsers(1);
            if (subTab === 'import_logs') fetchImportLogs(1, importStatusFilter);
            if (subTab === 'email_logs') fetchEmailLogs(1, emailStatusFilter);
        }
    }, [activeTab, subTab]);

    const handleMigrate = async () => {
        if (!file) return Swal.fire('Error', 'Please select a CSV file', 'error');

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true);
            let res;
            if (activeTab === 'users') {
                res = await migrateUsersApi(formData);
            } else if (activeTab === 'projects') {
                res = await migrateProjectsApi(formData);
            } else if (activeTab === 'roles') {
                res = await migrateRolesApi(formData);
            }

            if (res.data.success) {
                Swal.fire({
                    title: 'Import Completed!',
                    text: res.data.message || `${res.data.count} records processed!`,
                    icon: 'success'
                });
                if (activeTab === 'users') {
                    fetchStats();
                    fetchStagedUsers(1);
                    fetchImportLogs(1, importStatusFilter);
                }
            }
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } catch (err) {
            Swal.fire('Notice', err.response?.data?.message || 'Migration Failed', 'error');
            if (activeTab === 'users') {
                fetchImportLogs(1, importStatusFilter);
            }
        } finally {
            setLoading(false);
        }
    };


    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            return d.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch {
            return dateStr;
        }
    };

    const downloadSampleCsv = () => {
        let csvContent = '';
        let fileName = '';

        if (activeTab === 'users') {
            fileName = 'users-sample.csv';
            csvContent = `full_name,email,password,mobile_no,alternate_mobile_no,profile_image_url,education_level,talents,date_of_birth,gender,nationality,country_of_residence,preferred_currency,weight_kg,height_cm,eyes_color,skin_type,body_type,hair_type,hair_color,about_yourself,address_type,pin_code,address_line1,address_line2,state,district,role,is_verified,is_suspended,role_code,joget_user_id,joget_profile_id,facebook_url,instagram_url,linkedin_url,twitter_url,youtube_url,imdb_url,whatsapp_no,pan_number,aadhar_number,agency_name,website,company_incorporation_date,specialization,about_agency,gst_number,special_category,sports_type,esports_type,driving_type,people_appearance_list,know_more_count\nRahul Sharma,rahul@test.com,123456,9876543210,9876543211,https://test.com/profile.jpg,Graduate,Actor,1998-05-20,Male,Indian,India,INR,70,175,Brown,Fair,Athletic,Straight,Black,Professional Actor,Primary,110001,Connaught Place,Block A,Delhi,New Delhi,Candidate,1,0,300,U001,P001,https://fb.com/rahul,https://instagram.com/rahul,https://linkedin.com/in/rahul,https://twitter.com/rahul,https://youtube.com/rahul,https://imdb.com/rahul,9876543210,ABCDE1234F,123412341234,,,,,,GST001,Sports,Cricket,,Car Driving,Tall,10`;
        } else if (activeTab === 'projects') {
            fileName = 'projects-sample.csv';
            csvContent = `joget_project_id,project_name,project_type,director_name,producer_name,program_language,shoot_location_country,start_date,end_date,telecast_channel,type_of_genres,more_details,meta_title,meta_keywords,meta_description,project_image,shoot_locations_json,locations_summary_json,joget_user_id,request_data,status,created_by\nP001,Web Series One,Web Series,Rohit Kumar,Amit Sharma,Hindi,India,2025-01-01,2025-06-30,Netflix,Drama,Sample Project Description,Web Series Meta Title,webseries drama ott,Web Series Meta Description,https://test.com/project1.jpg,"[{""pincode"":""110001"",""addressLine1"":""Connaught Place"",""state"":""Delhi"",""district"":""New Delhi""}]","[{""district"":""New Delhi"",""state"":""Delhi"",""pincode"":""110001""}]",U001,,active,1`;
        } else if (activeTab === 'roles') {
            fileName = 'roles-sample.csv';
            csvContent = `audition_type,joget_role_id,role_name,role_category,project_joget_id,shoot_location,required_talent,required_gender,required_language,required_nationality,required_dialect,required_experience_level,required_skin_color,required_age,required_hair_color,required_body_type,required_waist_size,required_chest_size,required_hair_type,required_height,required_eye_color,special_category,sports_type,esports_type,driving_type,people_appearance_list,role_description,role_scene,story_line,character_sketch,role_script,meta_title,meta_keywords,meta_description,audition_locations_json,status,created_by,joget_user_id,request_data,final_amount\nOnline,R001,Lead Hero,Main Cast,P001,Mumbai,Actor,Male,Hindi,Indian,Mumbai,Experienced,Fair,25-35,Black,Athletic,32,40,Straight,175,Brown,Sports,Cricket,,Car Driving,Tall,Hero Character,Introduction Scene,Hero Story,Strong Character,Sample Script,Hero Meta Title,hero lead actor,Hero Meta Description,"[{""city"":""Mumbai"",""state"":""Maharashtra""}]",active,1,U001,,50000`;
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    };

    const getMigrationGuide = () => {
        if (activeTab === "users") {
            return `Always download and use new sample file
Steps to bulk upload Previous Users for Re-Launch Campaign:
1. Uploading CSV puts records into staging queue (NOT directly into active users).
2. Automated Background Jobs run 3 times daily at 9:00 AM, 2:00 PM, and 7:00 PM IST (10,000 - 15,000 emails per run).
3. System sends activation invite email using 'MIGRATION_RELAUNCH_INVITE' template.
4. Auto-Stop: When a user registers on the website, they are PERMANENTLY excluded from future emails.
5. Duplicates & Errors: Every single success and failure/skip is tracked with exact reason in the Logs tab below.

Mandatory Fields (*):
full_name * -> Full Name of User
email * -> Email Address (Must be valid & unique)
mobile_no -> Mobile Number (Checked for existing live users)
talents -> Actor, Model, Singer etc. (Uniqueness not checked)
joget_user_id -> OPTIONAL (Auto-generated UUID if missing)`;
        }
        if (activeTab === "projects") {
            return `Always download and use new sample file
Steps to bulk upload Projects:
1. Read notes carefully.
2. Download sample CSV file.
3. Mandatory Fields (*):
joget_project_id * -> Unique Project ID
project_name * -> Project Name
project_type * -> Movie / Web Series / TV Show
start_date -> YYYY-MM-DD
end_date -> YYYY-MM-DD`;
        }
        if (activeTab === "roles") {
            return `Always download and use new sample file
Steps to bulk upload Roles:
1. Mandatory Fields (*):
audition_type * -> Online / Offline
joget_role_id * -> Unique Role ID
role_name * -> Role Name
role_category * -> Main Cast / Supporting Cast
project_joget_id -> Matching Project ID`;
        }
        return "";
    };

    return (
        <div className="migration-page">
            <div className="migration-header">
                <h2>CSV DATA MIGRATION HUB</h2>
                <p>Import previous user leads, track success & failure reasons, and manage 3x daily re-launch email campaign</p>
            </div>

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => {
                    setActiveTab(k);
                    setFile(null);
                }}
                className="migration-tabs"
            >
                <Tab eventKey="users" title="Users Re-Launch Campaign" />
                <Tab eventKey="projects" title="Projects (CSV)" />
                <Tab eventKey="roles" title="Roles (CSV)" />
            </Tabs>

            {/* 🎯 USERS TAB: CAMPAIGN STATS & CONTROLS */}
            {activeTab === 'users' && (
                <>
                    {/* STATS CARDS */}
                    <Row className="campaign-stats-row mb-4">
                        <Col xs={12} sm={6} lg={3} className="mb-3">
                            <Card className="campaign-stat-card border-left-users">
                                <Card.Body>
                                    <div className="stat-icon-wrapper text-info">
                                        <FiUsers size={26} />
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-label">Total Staged Users</div>
                                        <h3 className="stat-value">
                                            {statsLoading ? <Spinner size="sm" /> : stats.total_staged}
                                        </h3>
                                        <small className="stat-sub">From CSV Import</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} lg={3} className="mb-3">
                            <Card className="campaign-stat-card border-left-sent">
                                <Card.Body>
                                    <div className="stat-icon-wrapper text-danger">
                                        <FiMail size={26} />
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-label">Total Emails Sent</div>
                                        <h3 className="stat-value">
                                            {statsLoading ? <Spinner size="sm" /> : stats.total_emails_sent}
                                        </h3>
                                        <small className="stat-sub">Across all batches</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} lg={3} className="mb-3">
                            <Card className="campaign-stat-card border-left-registered">
                                <Card.Body>
                                    <div className="stat-icon-wrapper text-success">
                                        <FiCheckCircle size={26} />
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-label">Registered on Web</div>
                                        <h3 className="stat-value text-success">
                                            {statsLoading ? <Spinner size="sm" /> : stats.registered}
                                        </h3>
                                        <small className="stat-sub text-success font-weight-bold">
                                            ✓ Auto-Excluded from emails
                                        </small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xs={12} sm={6} lg={3} className="mb-3">
                            <Card className="campaign-stat-card border-left-pending">
                                <Card.Body>
                                    <div className="stat-icon-wrapper text-warning">
                                        <FiClock size={26} />
                                    </div>
                                    <div className="stat-content">
                                        <div className="stat-label">Pending Activation</div>
                                        <h3 className="stat-value text-warning">
                                            {statsLoading ? <Spinner size="sm" /> : stats.pending}
                                        </h3>
                                        <small className="stat-sub">Receiving 3x daily cron</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* 🕒 AUTOMATION CRON BANNER & MANUAL DISPATCH */}
                    <Card className="campaign-schedule-card mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col lg={8} md={12} className="mb-3 mb-lg-0">
                                    <div className="d-flex align-items-center">
                                        <div className="cron-icon-badge">
                                            <FiClock size={24} />
                                        </div>
                                        <div className="ms-3">
                                            <h5 className="mb-1 text-white">
                                                Automated Re-Launch Drip Cron (3 Times Daily)
                                            </h5>
                                            <div className="cron-schedule-badges">
                                                {(stats.schedule_times && stats.schedule_times.length > 0 ? stats.schedule_times : ['09:00 AM IST', '02:00 PM IST', '07:00 PM IST']).map((timeStr, idx) => (
                                                    <Badge key={idx} bg="danger" className="me-2 px-2 py-1">🕒 {timeStr}</Badge>
                                                ))}
                                            </div>
                                            <p className="cron-desc mt-2 mb-0">
                                                Automatic background drip dispatches <strong>10,000 to 15,000 emails per run</strong> (~30,000 to 45,000/day). When a user registers or verifies OTP on BookMyAudition, they are <strong>immediately removed</strong> from all future batches.
                                            </p>
                                        </div>
                                    </div>
                                </Col>
                                <Col lg={4} md={12} className="text-lg-end mt-3 mt-lg-0">
                                    <div className="d-flex flex-wrap align-items-center justify-content-lg-end gap-2">
                                        <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3" style={{ background: 'rgba(40, 167, 69, 0.12)', border: '1px solid rgba(40, 167, 69, 0.35)' }}>
                                            <span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#28a745', boxShadow: '0 0 8px #28a745' }}></span>
                                            <span className="text-success small fw-semibold">Auto-Job Scheduled & Active</span>
                                        </div>

                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm" 
                                            onClick={() => { fetchStats(); fetchStagedUsers(stagedPage); }}
                                            disabled={statsLoading || stagedLoading}
                                            title="Refresh Campaign Stats"
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FiRefreshCw size={14} className={statsLoading ? 'spin-icon' : ''} />
                                            <span>Refresh</span>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </>
            )}

            {/* CSV UPLOAD CARD */}
            <Card className="migration-card mb-4">
                <Card.Body className="migration-card-body">
                    <h3 className="migration-title">
                        {activeTab.toUpperCase()} DATA MIGRATION
                    </h3>
                    <p className="migration-subtitle">
                        {activeTab === 'users' 
                            ? 'Upload previous candidates CSV. Data will be saved in staging queue with detailed success/failure logs.'
                            : 'Download sample file, prepare your CSV and import records securely.'}
                    </p>

                    <div className="migration-guide-box">
                        <div className="migration-guide-header">
                            CSV MIGRATION INSTRUCTIONS
                        </div>
                        <pre className="migration-guide-content">
                            {getMigrationGuide()}
                        </pre>
                    </div>

                    <Button
                        className="sample-download-btn"
                        onClick={downloadSampleCsv}
                    >
                        <FiDownload />
                        Download Sample CSV
                    </Button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        hidden
                        onChange={handleFileChange}
                    />

                    <div
                        className="upload-drop-area"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                        <FiUploadCloud size={80} />
                        <h5>Upload CSV File</h5>
                        <p>{file ? file.name : "Click here to browse CSV"}</p>
                    </div>

                    {file && (
                        <div className="selected-file">
                            <FiFileText />
                            {file.name}
                        </div>
                    )}

                    <Button
                        variant="danger"
                        onClick={handleMigrate}
                        disabled={loading || !file}
                        className="migration-import-btn"
                    >
                        {loading ? <Spinner size="sm" /> : `Import ${activeTab}`}
                    </Button>
                </Card.Body>
            </Card>

            {/* 📋 SECTION: QUEUE & DETAILED LOGS (SUCCESS & FAILURE TRACKING) */}
            {activeTab === 'users' && (
                <Card className="staged-table-card mb-4">
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        {/* SUB-TABS */}
                        <div className="d-flex gap-2">
                            <Button 
                                variant={subTab === 'queue' ? 'danger' : 'outline-secondary'} 
                                size="sm"
                                onClick={() => setSubTab('queue')}
                                className="d-flex align-items-center gap-1"
                            >
                                <FiUsers size={14} /> Active Queue ({totalRecords})
                            </Button>
                            <Button 
                                variant={subTab === 'import_logs' ? 'danger' : 'outline-secondary'} 
                                size="sm"
                                onClick={() => setSubTab('import_logs')}
                                className="d-flex align-items-center gap-1"
                            >
                                <FiList size={14} /> CSV Import Logs ({importLogsTotal})
                            </Button>
                            <Button 
                                variant={subTab === 'email_logs' ? 'danger' : 'outline-secondary'} 
                                size="sm"
                                onClick={() => setSubTab('email_logs')}
                                className="d-flex align-items-center gap-1"
                            >
                                <FiMail size={14} /> Email Dispatch Logs ({emailLogsTotal})
                            </Button>
                        </div>

                        {/* REFRESH & FILTERS */}
                        <div className="d-flex align-items-center gap-2">
                            {subTab === 'import_logs' && (
                                <Form.Select 
                                    size="sm"
                                    value={importStatusFilter}
                                    onChange={(e) => {
                                        setImportStatusFilter(e.target.value);
                                        fetchImportLogs(1, e.target.value);
                                    }}
                                    style={{ width: '130px', background: '#1c1c1c', color: '#fff', borderColor: '#333' }}
                                >
                                    <option value="ALL">All Status</option>
                                    <option value="SUCCESS">Success Only</option>
                                    <option value="SKIPPED">Skipped Only</option>
                                    <option value="FAILED">Failed Only</option>
                                </Form.Select>
                            )}

                            {subTab === 'email_logs' && (
                                <Form.Select 
                                    size="sm"
                                    value={emailStatusFilter}
                                    onChange={(e) => {
                                        setEmailStatusFilter(e.target.value);
                                        fetchEmailLogs(1, e.target.value);
                                    }}
                                    style={{ width: '130px', background: '#1c1c1c', color: '#fff', borderColor: '#333' }}
                                >
                                    <option value="ALL">All Status</option>
                                    <option value="SUCCESS">Sent (Success)</option>
                                    <option value="FAILED">Failed (Error)</option>
                                </Form.Select>
                            )}

                            <Button 
                                variant="outline-light" 
                                size="sm" 
                                onClick={() => {
                                    if (subTab === 'queue') fetchStagedUsers(stagedPage);
                                    if (subTab === 'import_logs') fetchImportLogs(importLogsPage, importStatusFilter);
                                    if (subTab === 'email_logs') fetchEmailLogs(emailLogsPage, emailStatusFilter);
                                }}
                            >
                                <FiRefreshCw size={12} className={(stagedLoading || importLogsLoading || emailLogsLoading) ? 'spin-icon' : ''} /> Refresh
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-0">
                        {/* 1. ACTIVE QUEUE TABLE */}
                        {subTab === 'queue' && (
                            stagedLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="danger" />
                                    <p className="text-muted mt-2">Loading queued candidates...</p>
                                </div>
                            ) : stagedUsers.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <FiAlertCircle size={36} className="mb-2" />
                                    <p>No previous candidates currently in staging queue. Import a CSV to begin.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover variant="dark" className="align-middle mb-0 staged-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Candidate Name</th>
                                                <th>Email</th>
                                                <th>Mobile</th>
                                                <th>Talents</th>
                                                <th className="text-center">Emails Sent</th>
                                                <th>Last Email Sent</th>
                                                <th className="text-center">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stagedUsers.map((item, idx) => (
                                                <tr key={item.id || idx}>
                                                    <td>{(stagedPage - 1) * 10 + idx + 1}</td>
                                                    <td className="font-weight-bold text-white">{item.full_name}</td>
                                                    <td>{item.email}</td>
                                                    <td>{item.mobile_no || '-'}</td>
                                                    <td><small className="text-muted">{item.talents || 'Actor'}</small></td>
                                                    <td className="text-center">
                                                        <Badge bg={item.email_sent_count > 0 ? "secondary" : "dark"} className="px-2 py-1">
                                                            {item.email_sent_count} sent
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {formatDate(item.last_email_sent_at)}
                                                        </small>
                                                    </td>
                                                    <td className="text-center">
                                                        {item.is_registered ? (
                                                            <Badge bg="success" className="px-2 py-1">
                                                                ✓ REGISTERED (STOPPED)
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="warning" text="dark" className="px-2 py-1">
                                                                ⏳ PENDING
                                                            </Badge>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )
                        )}

                        {/* 2. CSV IMPORT LOGS (SUCCESS / SKIPPED / FAILED WITH REASON) */}
                        {subTab === 'import_logs' && (
                            importLogsLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="danger" />
                                    <p className="text-muted mt-2">Loading import history logs...</p>
                                </div>
                            ) : importLogs.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <FiAlertCircle size={36} className="mb-2" />
                                    <p>No import logs found. Upload a CSV to view success and skip/failure records.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover variant="dark" className="align-middle mb-0 staged-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Candidate Name</th>
                                                <th>Email</th>
                                                <th>Mobile</th>
                                                <th className="text-center">Import Status</th>
                                                <th>Detailed Reason</th>
                                                <th>Imported At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importLogs.map((item, idx) => (
                                                <tr key={item.id || idx}>
                                                    <td>{(importLogsPage - 1) * 10 + idx + 1}</td>
                                                    <td className="font-weight-bold text-white">{item.full_name || 'Unknown'}</td>
                                                    <td>{item.email || <span className="text-danger">[Empty Email]</span>}</td>
                                                    <td>{item.mobile_no || '-'}</td>
                                                    <td className="text-center">
                                                        {item.status === 'SUCCESS' && (
                                                            <Badge bg="success" className="px-2 py-1">✓ SUCCESS</Badge>
                                                        )}
                                                        {item.status === 'SKIPPED' && (
                                                            <Badge bg="warning" text="dark" className="px-2 py-1">⚠️ SKIPPED</Badge>
                                                        )}
                                                        {item.status === 'FAILED' && (
                                                            <Badge bg="danger" className="px-2 py-1">❌ FAILED</Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: '12.5px', color: item.status === 'SUCCESS' ? '#28a745' : item.status === 'FAILED' ? '#ff4d4f' : '#faad14' }}>
                                                            {item.reason}
                                                        </span>
                                                    </td>
                                                    <td><small className="text-muted">{formatDate(item.created_at)}</small></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )
                        )}

                        {/* 3. EMAIL DISPATCH LOGS (SUCCESS & FAILURE WITH REASON) */}
                        {subTab === 'email_logs' && (
                            emailLogsLoading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="danger" />
                                    <p className="text-muted mt-2">Loading email delivery logs...</p>
                                </div>
                            ) : emailLogs.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <FiAlertCircle size={36} className="mb-2" />
                                    <p>No email dispatch logs found. Scheduled cron or manual batch runs will appear here.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <Table hover variant="dark" className="align-middle mb-0 staged-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Candidate Name</th>
                                                <th>Recipient Email</th>
                                                <th className="text-center">Delivery Status</th>
                                                <th>Delivery Details / Error Reason</th>
                                                <th>Sent At</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {emailLogs.map((item, idx) => (
                                                <tr key={item.id || idx}>
                                                    <td>{(emailLogsPage - 1) * 10 + idx + 1}</td>
                                                    <td className="font-weight-bold text-white">{item.full_name || 'Artist'}</td>
                                                    <td>{item.email}</td>
                                                    <td className="text-center">
                                                        {item.status === 'SUCCESS' ? (
                                                            <Badge bg="success" className="px-2 py-1">✓ DELIVERED</Badge>
                                                        ) : (
                                                            <Badge bg="danger" className="px-2 py-1">❌ FAILED</Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: '12.5px', color: item.status === 'SUCCESS' ? '#28a745' : '#ff4d4f' }}>
                                                            {item.reason}
                                                        </span>
                                                    </td>
                                                    <td><small className="text-muted">{formatDate(item.created_at)}</small></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )
                        )}
                    </Card.Body>

                    {/* PAGINATION */}
                    {subTab === 'queue' && totalPages > 1 && (
                        <Card.Footer className="d-flex justify-content-between align-items-center py-2">
                            <small className="text-muted">Page {stagedPage} of {totalPages}</small>
                            <Pagination size="sm" className="mb-0">
                                <Pagination.Prev 
                                    disabled={stagedPage === 1} 
                                    onClick={() => fetchStagedUsers(stagedPage - 1)} 
                                />
                                {[...Array(totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    if (pageNum === 1 || pageNum === totalPages || (pageNum >= stagedPage - 1 && pageNum <= stagedPage + 1)) {
                                        return (
                                            <Pagination.Item 
                                                key={pageNum} 
                                                active={pageNum === stagedPage}
                                                onClick={() => fetchStagedUsers(pageNum)}
                                            >
                                                {pageNum}
                                            </Pagination.Item>
                                        );
                                    }
                                    return null;
                                })}
                                <Pagination.Next 
                                    disabled={stagedPage === totalPages} 
                                    onClick={() => fetchStagedUsers(stagedPage + 1)} 
                                />
                            </Pagination>
                        </Card.Footer>
                    )}

                    {subTab === 'import_logs' && importLogsTotalPages > 1 && (
                        <Card.Footer className="d-flex justify-content-between align-items-center py-2">
                            <small className="text-muted">Page {importLogsPage} of {importLogsTotalPages} ({importLogsTotal} records)</small>
                            <Pagination size="sm" className="mb-0">
                                <Pagination.Prev 
                                    disabled={importLogsPage === 1} 
                                    onClick={() => fetchImportLogs(importLogsPage - 1, importStatusFilter)} 
                                />
                                <Pagination.Next 
                                    disabled={importLogsPage === importLogsTotalPages} 
                                    onClick={() => fetchImportLogs(importLogsPage + 1, importStatusFilter)} 
                                />
                            </Pagination>
                        </Card.Footer>
                    )}

                    {subTab === 'email_logs' && emailLogsTotalPages > 1 && (
                        <Card.Footer className="d-flex justify-content-between align-items-center py-2">
                            <small className="text-muted">Page {emailLogsPage} of {emailLogsTotalPages} ({emailLogsTotal} records)</small>
                            <Pagination size="sm" className="mb-0">
                                <Pagination.Prev 
                                    disabled={emailLogsPage === 1} 
                                    onClick={() => fetchEmailLogs(emailLogsPage - 1, emailStatusFilter)} 
                                />
                                <Pagination.Next 
                                    disabled={emailLogsPage === emailLogsTotalPages} 
                                    onClick={() => fetchEmailLogs(emailLogsPage + 1, emailStatusFilter)} 
                                />
                            </Pagination>
                        </Card.Footer>
                    )}
                </Card>
            )}

            <Alert className="migration-tip-box">
                <strong>Re-Launch Automation Tip:</strong> Background scheduler sends reactivation emails 3 times a day at 9:00 AM, 2:00 PM, and 7:00 PM IST. Any candidate who registers or verifies on the website is immediately excluded from future emails. All successes, skips, and failures are recorded in real-time above.
            </Alert>
        </div>
    );
};

export default MigrationPage;